import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { waitForPermissionSettled } from '@/composables/useGeolocationPermission';
import { config } from '@/config/config';
import { useUsageGate } from '@/composables/useUsageGate';

/**
 * `waitForPermissionSettled()`, bounded. A permission query that never
 * settles must not be able to stop a navigation from completing.
 */
async function _settledOrTimeout(): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      waitForPermissionSettled(),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, config.geolocation.permissionSettleTimeoutMs);
      })
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

const routes: RouteRecordRaw[] = [
  {
    // #525: the site root opens the tracker whenever the feature is on,
    // permission or not — #449 made the tracker usable without location,
    // and tracking is the flagship. (#56 used to redirect only on
    // 'granted'.) The /tracking guard still applies: feature flag off or
    // over the #58 quota resolve there. The BottomNav "home" tab targets
    // '/home' so tapping it always reaches HomeView.
    path: '/',
    name: 'root',
    component: () => import('@/views/HomeView.vue'),
    beforeEnter: (to) => {
      if (!config.features.contextualTracking) return true;
      return { path: '/tracking', query: to.query, hash: to.hash };
    }
  },
  {
    path: '/home',
    name: 'home',
    component: () => import('@/views/HomeView.vue')
  },
  {
    path: '/results',
    name: 'results',
    component: () => import('@/views/ResultsView.vue')
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/SettingsView.vue')
  },
  {
    path: '/saved',
    name: 'saved',
    component: () => import('@/views/SavedRoutesView.vue')
  },
  {
    path: '/tracking',
    name: 'tracking',
    component: () => import('@/views/TrackingView.vue'),
    // The permission no longer decides whether this route is REACHABLE
    // (#449) — only the feature flag and the usage gate do. It still
    // decides what happens once we are there, which is why the settle
    // wait below survives.
    beforeEnter: async () => {
      // PR #55 R5 — feature flag is authoritative. When off, deep-links
      // (e.g., a bookmarked /tracking URL from an internal QA session
      // that had the flag on) redirect to home.
      if (!config.features.contextualTracking) {
        return { path: '/home' };
      }
      // #449 SUPERSEDES #8 D3 and the PR #46 F6 redirect: a denied
      // permission no longer makes this view a dead end. The simulated leg
      // (#441) needs no location access, and being moved to /home told the
      // pilot nothing — the view's own placeholder says what is wrong and
      // how to fix it.
      //
      // PR #46 F1's settle wait STAYS, for a reason that is not this
      // guard's own branch. App.vue's watcher runs `immediate: true` the
      // moment TrackingView mounts; on a cold-start deep link it would read
      // the module default 'prompt' while the query is still in flight,
      // arm `watchPosition` for a user whose real state is 'denied', and on
      // iOS re-surface the native dialog. Letting the transition complete
      // first makes that ordering impossible.
      //
      // It is BOUNDED, though. An unbounded await on
      // `navigator.permissions.query` means that on an engine where the
      // promise never settles the tap does nothing and nothing paints.
      // Past the timeout we proceed on whatever we know.
      await _settledOrTimeout();
      // #58 usage gate — past the weekly quota the tracker is blocked too
      // (the flagship feature is where the install nudge bites). Uses the
      // persisted last-known count; never a network call, never burns quota,
      // and the kill-switch or a dev origin wins.
      if (useUsageGate().isOverQuota()) {
        return { name: 'quota-exceeded' };
      }
      return true;
    }
  },
  {
    path: '/quota-exceeded',
    name: 'quota-exceeded',
    component: () => import('@/views/QuotaInterstitial.vue')
  }
];

const router = createRouter({
  history: createWebHistory('/mobile/'),
  routes
});

export default router;
